import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { AIModelValueSet } from '~/common/model/api/ai-model';
import type {
  ProcessFlowStepTaskNewBatch,
  TaskConnector,
  TaskConnectorClassification,
  TaskConnectorSeparation,
} from '~/common/model/api/batch-type/process-flow';

import type { ConnectorSettingsFormField } from './_connector.type';

/**
 * Create ConnectorSettings FormField.
 *
 * @param {Nullable<ProcessFlowStepTaskNewBatch>} task ProcessFlowStep Task.
 *
 * @returns {ConnectorSettingsFormField} ConnectorSettings FormField.
 */
export function createConnectorSettingsFormField(task: Nullable<ProcessFlowStepTaskNewBatch>): ConnectorSettingsFormField {
  const result: ConnectorSettingsFormField = {};

  const separationAIModel = task?.settings.connectors.find((aiModel) => isType<TaskConnectorSeparation>(aiModel, 'separation'));
  const classificationAIModel = task?.settings.connectors.find((aiModel) => isType<TaskConnectorClassification>(aiModel, 'classification'));

  if (separationAIModel) {
    result.separationAIModel = {
      menuKey: separationAIModel.aiModel.provider,
      value: separationAIModel.aiModel.aiModelKey,
    };
  }

  if (classificationAIModel) {
    result.classificationAIModel = {
      menuKey: classificationAIModel.aiModel.provider,
      value: classificationAIModel.aiModel.aiModelKey,
    };
  }

  return result;
}

/**
 * Create ConnectorSettings.
 *
 * @param {ConnectorSettingsFormField} settings ConnectorSettings FormField.
 * @param {AIModelValueSet[]} aiModels AI Models.
 *
 * @returns {TaskConnector[]} ConnectorSettings.
 */
export function createConnectorSettings(
  { separationAIModel, classificationAIModel }: ConnectorSettingsFormField,
  aiModels: AIModelValueSet[] = [],
): TaskConnector[] {
  const result: TaskConnector[] = [];

  if (separationAIModel) {
    const aiModel = aiModels.find(
      ({ provider, type, aiModelKey }) => type === 'separation' && provider === separationAIModel.menuKey && aiModelKey === separationAIModel.value,
    );
    if (aiModel) {
      result.push({
        id: newKey(),
        $is: 'separation',
        aiModel,
      });
    }
  }

  if (classificationAIModel) {
    const aiModel = aiModels.find(
      ({ provider, type, aiModelKey }) => type === 'classification' && provider === classificationAIModel.menuKey && aiModelKey === classificationAIModel.value,
    );
    if (aiModel) {
      result.push({
        id: newKey(),
        $is: 'classification',
        aiModel,
      });
    }
  }

  return result;
}
